import { Field } from '@catering/ui';
import { type JSX, useEffect, useRef, useState } from 'react';

import {
  CUSTOMER_STATUSES,
  type CustomerStatus,
  ZONES,
  ZONE_LABELS,
  type Zone,
} from '../../../customers/api';

const SEARCH_DEBOUNCE_MS = 300;

const STATUS_LABELS: Record<CustomerStatus, string> = {
  ACTIVE: 'Activas',
  SUSPENDED: 'Suspendidas',
};

export interface CustomerSearchBarProps {
  query: string;
  zone: Zone | undefined;
  status: CustomerStatus | undefined;
  onQueryChange: (query: string) => void;
  onZoneChange: (zone: Zone | undefined) => void;
  onStatusChange: (status: CustomerStatus | undefined) => void;
}

/**
 * The text field debounces locally; zone/status are `<select>`s that commit
 * immediately, per `design.md` §7 (no shared `Select` component yet).
 */
export function CustomerSearchBar({
  query,
  zone,
  status,
  onQueryChange,
  onZoneChange,
  onStatusChange,
}: CustomerSearchBarProps): JSX.Element {
  const [draft, setDraft] = useState(query);
  const [syncedQuery, setSyncedQuery] = useState(query);

  // Keeps the draft aligned when the committed query changes from outside
  // (e.g. browser back/forward) without fighting the user's own typing.
  // Adjusted directly during render, per React's "state derived from
  // props" pattern, rather than in an effect.
  if (query !== syncedQuery) {
    setSyncedQuery(query);
    setDraft(query);
  }

  const onQueryChangeRef = useRef(onQueryChange);
  useEffect(() => {
    onQueryChangeRef.current = onQueryChange;
  });

  useEffect(() => {
    const trimmed = draft.trim();

    if (trimmed === query) {
      return;
    }

    const timeout = setTimeout(() => onQueryChangeRef.current(trimmed), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [draft, query]);

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="min-w-64 flex-1">
        <Field
          label="Buscar"
          name="q"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Razón social, nombre comercial o CUIT"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="customer-zone-filter" className="text-sm font-medium text-slate-900">
          Zona
        </label>
        <select
          id="customer-zone-filter"
          value={zone ?? ''}
          onChange={(event) =>
            onZoneChange(event.target.value === '' ? undefined : (event.target.value as Zone))
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todas las zonas</option>
          {ZONES.map((z) => (
            <option key={z} value={z}>
              {ZONE_LABELS[z]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="customer-status-filter" className="text-sm font-medium text-slate-900">
          Estado
        </label>
        <select
          id="customer-status-filter"
          value={status ?? ''}
          onChange={(event) =>
            onStatusChange(
              event.target.value === '' ? undefined : (event.target.value as CustomerStatus),
            )
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todas</option>
          {CUSTOMER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
